import { Link } from "react-router";
import { CONDITION_LABEL, LANGUAGE_LABEL } from "../../shared/variants";
import { PROVINCES, type WantedCard, type WantedPost } from "../../shared/wanted";
import { useI18n } from "../i18n/I18nProvider";
import { imageUrl } from "../lib/api";
import { useArs } from "../market/components";
import { ReportButton } from "../market/components";

const DAY_MS = 86_400_000;

/** "hoy", "hace 1 día", "hace 12 días". */
export function useAgo() {
  const { t } = useI18n();
  return (iso: string) => {
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS);
    return days < 1 ? t("wanted.today") : t("wanted.ago", { count: days });
  };
}

/** One card on the board: how many want it, never who. */
export function WantedTile({ card }: { card: WantedCard }) {
  const { t } = useI18n();
  const ars = useArs();
  const shown = card.provinces.slice(0, 2);
  return (
    <li className="wanted-tile">
      <Link to={`/se-busca/${encodeURIComponent(card.slug)}`}>
        <img src={imageUrl(card.imageKey)} alt="" width={160} height={224} loading="lazy" decoding="async" />
        <span className="wanted-tile-body">
          <strong className="wanted-tile-name">{card.name}</strong>
          <span className="muted small">{[card.setName, card.cardNumber ? `#${card.cardNumber}` : null].filter(Boolean).join(" · ")}</span>
          <span className="wanted-count">{t("wanted.wanting", { count: card.wanting })}</span>
          <span className="wanted-chips">
            {card.trade && <span className="chip chip-trade">🔁 {t("wanted.trade")}</span>}
            {shown.map((p) => (
              <span key={p} className="chip">
                {PROVINCES[p]}
              </span>
            ))}
            {card.provinces.length > shown.length && <span className="chip">{t("wanted.moreProvinces", { count: card.provinces.length - shown.length })}</span>}
          </span>
          {card.market && <span className="muted small">{t("wanted.onSale", { price: ars(card.market.minPriceArs), count: card.market.count })}</span>}
        </span>
      </Link>
    </li>
  );
}

/** One person's post on a card's page (and the form's preview). `report`: the 🚩 button (not in the preview). */
export function WantedPostCard({ post, sealed, report = true }: { post: WantedPost; sealed: boolean; report?: boolean }) {
  const { t, locale } = useI18n();
  const ars = useArs();
  const ago = useAgo();
  const facts = [
    sealed ? null : post.conditionMin ? t("wanted.minCondition", { condition: CONDITION_LABEL[post.conditionMin][locale] }) : t("wanted.anyCondition"),
    post.language ? (LANGUAGE_LABEL[post.language]?.[locale] ?? post.language) : null,
    post.quantity > 1 ? t("wanted.qty", { count: post.quantity }) : null,
  ].filter(Boolean);
  return (
    <article className="wanted-post">
      <header className="wanted-post-head">
        <strong>{post.handle ? <Link to={`/vendedores/${post.handle}`}>{post.alias}</Link> : post.alias}</strong>
        <span className="muted small">
          {post.province ? `${PROVINCES[post.province]} · ` : ""}
          {ago(post.since)}
        </span>
      </header>
      <p className="wanted-post-facts small">
        {facts.join(" · ")}
        {post.trade && <span className="chip chip-trade">🔁 {t("wanted.trade")}</span>}
        {post.maxPriceArs !== null && <span className="chip chip-price">{t("wanted.upTo", { price: ars(post.maxPriceArs) })}</span>}
      </p>
      {post.note && <p className="wanted-post-note">«{post.note}»</p>}
      {report && post.id > 0 && <ReportButton target="want" targetId={String(post.id)} />}
    </article>
  );
}
